/**
 * /data-migration — Account Startup (Data Migration) + Account Update tool.
 *
 * Two jobs, one screen:
 *   • STARTUP / MIGRATION — bring a company's books over from a prior system
 *     (ProContractor, QuickBooks, Sage…). Opening AR/AP carry their balance-
 *     sheet side and offset to Opening Balance Equity (no re-recognized P&L);
 *     the trial balance auto-excludes the AR/AP control accounts (the detail
 *     builds them) and plugs to OBE. The whole cutover ties out when OBE = 0.
 *   • UPDATE — load more data into a live company (links to the power-user
 *     /imports screen; same importers, normal posting).
 *   • PARALLEL CLOSE — compare incumbent and Ledger Core trial balances for
 *     consecutive closed periods and preserve exact-cent cutover evidence.
 *
 * The wizard reuses the universal importer hooks — every step is an
 * inspect → map columns → preview → commit cycle, then a reconciliation
 * panel that reads the one number that proves the migration: OBE.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";

import {
  migrationReconciliationQueryOptions,
  useInspectImport,
  useInterpretImport,
  useUploadCommitImport,
  useUploadPreviewImport,
  type FileImportResult,
  type InspectResponse,
} from "@/lib/imports";
import { useToast } from "@/components/Toaster";
import { ParallelCloseToolkit } from "@/components/ParallelCloseToolkit";

export const Route = createFileRoute("/_auth/data-migration")({
  component: DataMigrationPage,
});

// ──────────────────────────────────────────────────────────────
// The migration sequence. Order matters: chart of accounts first
// (so GL codes resolve), then the AR/AP detail (which builds the
// control balances), then the trial balance (which excludes them).
// ──────────────────────────────────────────────────────────────
type StepDef = {
  kind: string;
  title: string;
  blurb: string;
  context: Record<string, unknown>;
  needsAsOf?: boolean;
};

const MIGRATION_STEPS: StepDef[] = [
  {
    kind: "chart_of_accounts",
    title: "Chart of Accounts",
    blurb:
      "Your GL account list. Everything else references these codes, so it goes first.",
    context: {},
  },
  {
    kind: "ar_invoices",
    title: "Open Receivables (AR)",
    blurb:
      "Your open customer invoices as of the cutover date. No GL account needed — each one carries the receivable and offsets to Opening Balance Equity. Missing customers are created automatically.",
    context: { mode: "opening", auto_create_parties: true },
  },
  {
    kind: "ap_invoices",
    title: "Open Payables (AP)",
    blurb:
      "Your open vendor bills as of the cutover date. No GL account needed — each one carries the payable and offsets to Opening Balance Equity. Missing vendors are created automatically.",
    context: { mode: "opening", auto_create_parties: true },
  },
  {
    kind: "fixed_assets",
    title: "Fixed Assets",
    blurb:
      "Your fixed asset register — cost, accumulated depreciation, and useful life for each asset. This creates the asset records only; no journals post here. The balances arrive with the trial balance below, and the asset-GL tie-out report is the reconciliation.",
    context: {},
  },
  {
    kind: "opening_trial_balance",
    title: "Trial Balance",
    blurb:
      "Your full trial balance as of the cutover date. The AR/AP control accounts are excluded automatically (the detail above already built them) and the difference plugs to Opening Balance Equity.",
    context: { mode: "opening" },
    needsAsOf: true,
  },
];

function DataMigrationPage() {
  const [mode, setMode] = useState<null | "migrate" | "update" | "parallel">(null);

  if (mode === null) return <ModeChooser onPick={setMode} />;
  if (mode === "update") return <UpdateMode onBack={() => setMode(null)} />;
  if (mode === "parallel") return <ParallelCloseToolkit onBack={() => setMode(null)} />;
  return <MigrateWizard onBack={() => setMode(null)} />;
}

// ──────────────────────────────────────────────────────────────
// Intro — pick what you're doing
// ──────────────────────────────────────────────────────────────
function ModeChooser({ onPick }: { onPick: (m: "migrate" | "update" | "parallel") => void }) {
  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Migration &amp; Setup
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Bring a company's books over, load more data into a live company, or
        prove consecutive period-end parity before replacing the incumbent.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <ChoiceCard
          title="Set up a new company"
          tag="Startup / Migration"
          desc="First-time cutover from ProContractor, QuickBooks, Sage, etc. Carries opening balances over correctly so the balance sheet ties out — guided, five files, one final check."
          cta="Start migration"
          onClick={() => onPick("migrate")}
        />
        <ChoiceCard
          title="Update a live company"
          tag="Ongoing import"
          desc="Add transactions to a company that's already running — normal posting to the P&L and GL. Opens the universal import screen."
          cta="Open importer"
          variant="outlined"
          onClick={() => onPick("update")}
        />
        <ChoiceCard
          title="Prove a parallel close"
          tag="Migration assurance"
          desc="Load the incumbent period-end trial balance, map accounts, compare every non-zero balance to Ledger Core, resolve exceptions, and certify consecutive closes to the penny."
          cta="Open proof toolkit"
          variant="outlined"
          onClick={() => onPick("parallel")}
        />
      </Box>
    </Box>
  );
}

function ChoiceCard({
  title,
  tag,
  desc,
  cta,
  onClick,
  variant = "contained",
}: {
  title: string;
  tag: string;
  desc: string;
  cta: string;
  onClick: () => void;
  variant?: "contained" | "outlined";
}) {
  return (
    <Paper variant="outlined" sx={{ p: 3, flex: "1 1 380px", minWidth: 320 }}>
      <Chip label={tag} size="small" color="primary" variant="outlined" sx={{ mb: 1 }} />
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary" sx={{ my: 1.5, minHeight: 84 }}>
        {desc}
      </Typography>
      <Button variant={variant} onClick={onClick}>
        {cta}
      </Button>
    </Paper>
  );
}

function UpdateMode({ onBack }: { onBack: () => void }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ maxWidth: 720, mx: "auto", p: 3 }}>
      <Button onClick={onBack} sx={{ mb: 2 }}>
        ← Back
      </Button>
      <Typography variant="h5" gutterBottom>
        Update a live company
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Ongoing imports post normally (real revenue/expense to the P&amp;L). Use
        the universal import screen — it covers bank transactions, AR, AP,
        journals and more, each with column mapping.
      </Alert>
      <Button variant="contained" onClick={() => navigate({ to: "/imports" })}>
        Open the import screen
      </Button>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────
// The guided migration
// ──────────────────────────────────────────────────────────────
function MigrateWizard({ onBack }: { onBack: () => void }) {
  const [active, setActive] = useState(0);
  const [done, setDone] = useState<boolean[]>(() => MIGRATION_STEPS.map(() => false));
  const reconcileStep = MIGRATION_STEPS.length;

  const markDone = (i: number) => {
    setDone((d) => d.map((v, idx) => (idx === i ? true : v)));
    setActive(i + 1);
  };

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", p: 3 }}>
      <Button onClick={onBack} sx={{ mb: 2 }}>
        ← Back
      </Button>
      <Typography variant="h4" gutterBottom>
        Account Startup — Data Migration
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Drop one export per step. Each is dry-run previewed before anything is
        written. When you finish, the reconciliation panel confirms the cutover
        ties out.
      </Typography>

      <Stepper activeStep={active} orientation="vertical">
        {MIGRATION_STEPS.map((step, i) => (
          <Step key={step.kind} completed={done[i]}>
            <StepLabel
              optional={
                done[i] ? <Typography variant="caption">Imported</Typography> : undefined
              }
              sx={{ cursor: "pointer" }}
              onClick={() => setActive(i)}
            >
              {step.title}
            </StepLabel>
            {active === i && (
              <Box sx={{ pl: 1, pb: 2 }}>
                <MigrationStep
                  step={step}
                  onDone={() => markDone(i)}
                  onSkip={() => setActive(i + 1)}
                />
              </Box>
            )}
          </Step>
        ))}
        <Step completed={false}>
          <StepLabel onClick={() => setActive(reconcileStep)} sx={{ cursor: "pointer" }}>
            Reconcile &amp; finish
          </StepLabel>
          {active === reconcileStep && (
            <Box sx={{ pl: 1, pb: 2 }}>
              <ReconcilePanel />
            </Box>
          )}
        </Step>
      </Stepper>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────
// One file step: inspect → map → preview → commit
// ──────────────────────────────────────────────────────────────
function MigrationStep({
  step,
  onDone,
  onSkip,
}: {
  step: StepDef;
  onDone: () => void;
  onSkip: () => void;
}) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [inspect, setInspect] = useState<InspectResponse | null>(null);
  // {target_field: source_header} — the live mapping the conversation refines.
  const [map, setMap] = useState<Record<string, string>>({});
  const [asOf, setAsOf] = useState("");
  const [preview, setPreview] = useState<FileImportResult | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");

  const inspectM = useInspectImport();
  const interpretM = useInterpretImport();
  const previewM = useUploadPreviewImport();
  const commitM = useUploadCommitImport();

  const context = useMemo(() => {
    const c = { ...step.context };
    if (step.needsAsOf && asOf) (c as Record<string, unknown>).as_of_date = asOf;
    return c;
  }, [step, asOf]);

  // {source_header: target_field} for the import API.
  const columnMap = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [target, source] of Object.entries(map)) {
      if (source) out[source] = target;
    }
    return out;
  }, [map]);

  const requiredUnmapped = (inspect?.columns ?? [])
    .filter((c) => c.required && !map[c.name])
    .map((c) => c.name);

  const onPickFile = async (f: File | null) => {
    setFile(f);
    setInspect(null);
    setPreview(null);
    setMap({});
    setMessages([]);
    if (!f) return;
    const res = await inspectM.mutateAsync({ kind: step.kind, file: f });
    setInspect(res);
    setMap({ ...res.suggested_map });
    setMessages([{ role: "agent", text: openingMessage(res) }]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !inspect) return;
    setInput("");
    setMessages((m) => [...m, { role: "you", text }]);
    try {
      const res = await interpretM.mutateAsync({
        kind: step.kind,
        source_headers: inspect.source_headers,
        sample_rows: inspect.sample_rows,
        current_map: map,
        feedback: text,
      });
      setMap(res.proposed_mapping);
      setPreview(null); // mapping changed — last preview is stale
      const note = res.unmapped_required.length
        ? `\n\n(Still need a column for: ${res.unmapped_required.join(", ")} — or I can auto-create where that applies.)`
        : "";
      setMessages((m) => [...m, { role: "agent", text: res.reply + note }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "agent", text: "Sorry — I couldn't process that just now. Try rephrasing, or set the column directly." },
      ]);
    }
  };

  const runPreview = async () => {
    if (!file) return;
    if (step.needsAsOf && !asOf) {
      toast.error("Enter the cutover date first.");
      return;
    }
    const res = await previewM.mutateAsync({ kind: step.kind, file, context, column_map: columnMap });
    setPreview(res);
  };

  const runCommit = async () => {
    if (!file) return;
    const res = await commitM.mutateAsync({ kind: step.kind, file, context, column_map: columnMap });
    const r = res.result;
    if (r && r.error_count > 0) {
      toast.error(`${r.error_count} row(s) errored — tell me what's wrong above.`);
      setPreview(res);
      return;
    }
    toast.success(`Imported ${r?.created_count ?? 0} record(s).`);
    onDone();
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography color="text.secondary" sx={{ mb: 1.5 }}>
        {step.blurb}
      </Typography>

      {step.needsAsOf && (
        <TextField
          type="date"
          label="Cutover date (as of)"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ mb: 2, display: "block" }}
          helperText="Usually the last day of the closed period in your old system."
        />
      )}

      <Button variant="outlined" component="label" disabled={inspectM.isPending}>
        {file ? `Change file (${file.name})` : "Choose CSV / Excel file"}
        <input
          hidden
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
        />
      </Button>
      {inspectM.isPending && <CircularProgress size={18} sx={{ ml: 2 }} />}

      {inspect && (
        <Box sx={{ mt: 2 }}>
          {/* The conversation — the system reads your file and you correct it in plain words. */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
              maxHeight: 320,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              bgcolor: "action.hover",
            }}
          >
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  alignSelf: m.role === "you" ? "flex-end" : "flex-start",
                  maxWidth: "85%",
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: m.role === "you" ? "primary.main" : "background.paper",
                  color: m.role === "you" ? "primary.contrastText" : "text.primary",
                  whiteSpace: "pre-wrap",
                  fontSize: 14,
                }}
              >
                {m.text}
              </Box>
            ))}
            {interpretM.isPending && (
              <Box sx={{ alignSelf: "flex-start", px: 1 }}>
                <CircularProgress size={16} />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={'e.g. "that column is the customer name, not the code"'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={interpretM.isPending}
            />
            <Button onClick={send} disabled={interpretM.isPending || !input.trim()}>
              Send
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={runPreview}
              disabled={previewM.isPending || requiredUnmapped.length > 0}
            >
              {previewM.isPending ? "Previewing…" : "Preview"}
            </Button>
            <Button
              variant="contained"
              onClick={runCommit}
              disabled={commitM.isPending || !preview}
            >
              {commitM.isPending ? "Importing…" : "Import"}
            </Button>
            <Button onClick={onSkip} color="inherit">
              Skip
            </Button>
            {requiredUnmapped.length > 0 && (
              <Typography variant="caption" color="error">
                Still need: {requiredUnmapped.join(", ")} — tell me which column, above.
              </Typography>
            )}
          </Box>

          {preview?.preview && (
            <PreviewSummary
              ok={preview.preview.will_create_count}
              dup={preview.preview.duplicate_count}
              err={preview.preview.error_count}
              rows={preview.preview.rows.slice(0, 8).map((r) => ({
                status: r.status,
                message: r.message,
              }))}
            />
          )}
        </Box>
      )}
    </Paper>
  );
}

type ChatMsg = { role: "agent" | "you"; text: string };

/** The agent's first message: how it read the file, in plain English. */
function openingMessage(insp: InspectResponse): string {
  const pairs = Object.entries(insp.suggested_map); // target -> source
  const lines = pairs
    .map(([t, s]) => `  • your “${s}” → ${t.replace(/_/g, " ")}`)
    .join("\n");
  const lead =
    insp.mapped_by?.startsWith("llm")
      ? `I read your file (${insp.parsed_row_count} rows). Here's how I'll bring it in:`
      : `I matched your columns automatically (${insp.parsed_row_count} rows):`;
  const notes = insp.data_notes?.length
    ? `\n\nA couple of things I noticed:\n` +
      insp.data_notes.map((n) => `  • ${n}`).join("\n")
    : "";
  return `${lead}\n${lines}${notes}\n\nTell me anything that's off — e.g. "that column is the name, not the code" — or click Preview to see a dry run.`;
}

function PreviewSummary({
  ok,
  dup,
  err,
  rows,
}: {
  ok: number;
  dup: number;
  err: number;
  rows: { status: string; message: string | null }[];
}) {
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Chip label={`${ok} will import`} color="success" size="small" />
        {dup > 0 && <Chip label={`${dup} duplicate`} size="small" />}
        {err > 0 && <Chip label={`${err} error`} color="error" size="small" />}
      </Box>
      {rows
        .filter((r) => r.message)
        .map((r, i) => (
          <Typography
            key={i}
            variant="caption"
            color={r.status === "error" ? "error" : "text.secondary"}
            sx={{ display: "block" }}
          >
            • {r.message}
          </Typography>
        ))}
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────
// The proof: Opening Balance Equity must be zero
// ──────────────────────────────────────────────────────────────
function ReconcilePanel() {
  const { data, isLoading, refetch, isFetching } = useQuery(
    migrationReconciliationQueryOptions(),
  );

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Does it tie out?
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Every opening entry offsets to <strong>Opening Balance Equity</strong>.
        When the AR/AP detail matches the trial balance, OBE is exactly zero.
      </Typography>

      {isLoading ? (
        <CircularProgress size={22} />
      ) : data ? (
        <>
          <Alert
            severity={data.is_balanced ? "success" : "warning"}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" sx={{ fontVariantNumeric: "tabular-nums" }}>
              Opening Balance Equity: {data.obe_balance}
            </Typography>
            {data.note}
          </Alert>
          {data.controls.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Control accounts (built from your detail)
              </Typography>
              {data.controls.map((c) => (
                <Typography key={c.role} variant="body2" color="text.secondary">
                  {c.code} {c.name}: {c.balance}
                </Typography>
              ))}
            </Box>
          )}
        </>
      ) : (
        <Alert severity="error">Could not load the reconciliation.</Alert>
      )}

      <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>
        {isFetching ? "Checking…" : "Re-check"}
      </Button>
    </Paper>
  );
}
